import type {
  RankBoard,
  RankingsResponse,
  AccountResponse,
  ApiErrorResponse,
  BossHitRequest,
  CloudSaveResponse,
  ConfigResponse,
  GoogleAuthResponse,
  PutSaveResponse,
  BossResponse,
  ClassBattleResponse,
  ClassDTO,
  ClassResponse,
  FriendNotebookResponse,
  NotebookCardDTO,
  DareCreateRequest,
  DareDTO,
  DareResponse,
  DaresResponse,
  FanMailResponse,
  FightReactionRequest,
  FightReportRequest,
  FightReportResponse,
  FightResult,
  BattleRecordDTO,
  BattlesResponse,
  ContestEntriesResponse,
  ContestEntryDTO,
  ContestEntryResponse,
  ContestResponse,
  FriendDTO,
  FriendRequestRequest,
  FriendResponse,
  FriendsResponse,
  GetDrawingResponse,
  LeaderboardEntryDTO,
  LeaderboardResponse,
  PlayerResponse,
  CreatePlayerRequest,
  CreatePlayerResponse,
  PatchPlayerRequest,
  DrawingDTO,
  GetSharedMonsterResponse,
  HealthResponse,
  MonsterDTO,
  PlayerDTO,
  ShareMonsterResponse,
  UploadDrawingResponse,
} from '@shared/protocol/messages';
import { t } from '../i18n';
import { CONSENT_VERSION } from '../ui/consent';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** REST client for the Go server. All calls time out and surface friendly errors. */
export class ApiClient {
  token: string | null = null;
  readonly base: string;

  constructor(base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '') {
    this.base = base.replace(/\/$/, '');
  }

  get wsUrl(): string {
    if (this.base) return this.base.replace(/^http/, 'ws') + '/ws';
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/ws`;
  }

  private async req<T>(method: string, path: string, body?: unknown, timeoutMs = 6000): Promise<T> {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      if (this.token) headers.Authorization = `Bearer ${this.token}`;
      const init: RequestInit = { method, headers, signal: ctrl.signal };
      if (body !== undefined) init.body = JSON.stringify(body);
      const res = await fetch(this.base + path, init);
      const text = await res.text();
      let data: unknown = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        throw new ApiError(res.status, 'bad_response', t('The server sent a scribble we could not read.'));
      }
      if (!res.ok) {
        const err = (data as ApiErrorResponse | null)?.error;
        throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? t('Request failed ({status})', { status: res.status }));
      }
      return data as T;
    } catch (err) {
      if (err instanceof ApiError) throw err;
      if (err instanceof DOMException && err.name === 'AbortError') throw new ApiError(0, 'timeout', t('The server took too long to answer.'));
      throw new ApiError(0, 'offline', t('Cannot reach the game server. Single-player still works!'));
    } finally {
      window.clearTimeout(timer);
    }
  }

  /**
   * Short-lived copies of read-only answers that pages ask for again on every
   * redraw (friends, dares, contest…): the server reads its database for each.
   * Any change made through this client, or pushed by the server, clears them.
   */
  private memoStore = new Map<string, { at: number; p: Promise<unknown> }>();

  private memo<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> {
    const k = `${this.token ?? ''}|${key}`;
    const hit = this.memoStore.get(k);
    if (hit && Date.now() - hit.at < ttlMs) return hit.p as Promise<T>;
    const p = fn();
    this.memoStore.set(k, { at: Date.now(), p });
    // A failed answer is not kept: the next ask tries again.
    p.catch(() => this.memoStore.get(k)?.p === p && this.memoStore.delete(k));
    return p;
  }

  /** Drops cached answers (after a change, or when the server says something changed). */
  forget(...keys: string[]): void {
    for (const k of [...this.memoStore.keys()]) if (keys.some((key) => k.endsWith(`|${key}`))) this.memoStore.delete(k);
  }

  /** Any JSON endpoint (newer Cloudflare-only routes such as the shop). */
  raw<T>(method: string, path: string, body?: unknown): Promise<T> {
    return this.req<T>(method, path, body, 15000);
  }
  health(): Promise<HealthResponse> {
    return this.req('GET', '/api/health', undefined, 2500);
  }
  createPlayer(name: string, kid?: { lang: 'en' | 'th' }): Promise<CreatePlayerResponse> {
    const body: CreatePlayerRequest = kid ? { name, kid: true, lang: kid.lang, consent: CONSENT_VERSION } : { name, consent: CONSENT_VERSION };
    return this.req('POST', '/api/players', body);
  }
  me(): Promise<PlayerDTO> {
    return this.memo('me', 60_000, async () => (await this.req<PlayerResponse>('GET', '/api/players/me')).player);
  }
  async rename(name: string): Promise<PlayerDTO> {
    return this.patchMe({ name });
  }
  /** Rename, reroll a kid-mode name, or switch kid mode. */
  async patchMe(body: PatchPlayerRequest): Promise<PlayerDTO> {
    this.forget('me', 'friends');
    return (await this.req<PlayerResponse>('PATCH', '/api/players/me', body)).player;
  }
  /* ---------- accounts & cloud save (Cloudflare server; the Go dev server answers 404) ---------- */
  config(): Promise<ConfigResponse> {
    return this.req('GET', '/api/config', undefined, 4000);
  }
  googleSignIn(credential: string, kid?: { lang: 'en' | 'th' }): Promise<GoogleAuthResponse> {
    return this.req('POST', '/api/auth/google', kid ? { credential, kid: true, lang: kid.lang, consent: CONSENT_VERSION } : { credential, consent: CONSENT_VERSION });
  }
  googleLink(credential: string): Promise<AccountResponse> {
    this.forget('account');
    return this.req('POST', '/api/auth/google/link', { credential });
  }
  account(): Promise<AccountResponse> {
    return this.memo('account', 300_000, () => this.req('GET', '/api/account'));
  }
  getSave(): Promise<CloudSaveResponse> {
    return this.req('GET', '/api/save', undefined, 15000);
  }
  /** Rejects with status 409 when the cloud copy moved on since baseRev. */
  putSave(save: unknown, baseRev: number): Promise<PutSaveResponse> {
    return this.req('PUT', '/api/save', { save, baseRev }, 15000);
  }
  uploadDrawing(drawing: DrawingDTO): Promise<UploadDrawingResponse> {
    return this.req('POST', '/api/drawings', { drawing });
  }
  async getDrawing(id: string): Promise<DrawingDTO> {
    return (await this.req<GetDrawingResponse>('GET', `/api/drawings/${encodeURIComponent(id)}`)).drawing;
  }
  shareMonster(monster: MonsterDTO): Promise<ShareMonsterResponse> {
    return this.req('POST', '/api/monsters', { monster });
  }
  getSharedMonster(code: string): Promise<GetSharedMonsterResponse> {
    return this.req('GET', `/api/monsters/${encodeURIComponent(code.trim().toUpperCase())}`);
  }
  battles(): Promise<BattleRecordDTO[]> {
    return this.memo('battles', 60_000, async () => (await this.req<BattlesResponse>('GET', '/api/battles')).battles);
  }
  /** One ranking board (works signed out; signed in it also says where I stand). */
  rankings(board: RankBoard): Promise<RankingsResponse> {
    return this.req('GET', `/api/rankings?board=${board}`);
  }
  leaderboard(): Promise<LeaderboardEntryDTO[]> {
    return this.memo('leaderboard', 120_000, async () => (await this.req<LeaderboardResponse>('GET', '/api/leaderboard')).players);
  }
  friends(): Promise<FriendsResponse> {
    return this.memo('friends', 30_000, () => this.req('GET', '/api/friends'));
  }
  /** Send a request by typed friend code or by player id (a recent opponent). */
  async addFriend(target: FriendRequestRequest): Promise<FriendDTO> {
    this.forget('friends');
    return (await this.req<FriendResponse>('POST', '/api/friends/requests', target)).friend;
  }
  async acceptFriend(playerId: string): Promise<FriendDTO> {
    this.forget('friends');
    return (await this.req<FriendResponse>('POST', `/api/friends/${encodeURIComponent(playerId)}/accept`)).friend;
  }
  /** Decline an incoming request, cancel an outgoing one, or unfriend. */
  async removeFriend(playerId: string): Promise<void> {
    this.forget('friends');
    await this.req<null>('DELETE', `/api/friends/${encodeURIComponent(playerId)}`);
  }

  /** The open contest round: approved entries, my entry and votes left. */
  contest(): Promise<ContestResponse> {
    return this.memo('contest', 60_000, () => this.req('GET', '/api/contest'));
  }
  async submitContest(monster: MonsterDTO): Promise<ContestEntryDTO> {
    this.forget('contest');
    return (await this.req<ContestEntryResponse>('POST', '/api/contest/entries', { monster })).entry;
  }
  async withdrawContest(entryId: string): Promise<void> {
    this.forget('contest');
    await this.req<null>('DELETE', `/api/contest/entries/${encodeURIComponent(entryId)}`);
  }
  async vote(entryId: string, on: boolean): Promise<void> {
    this.forget('contest');
    await this.req<null>(on ? 'POST' : 'DELETE', `/api/contest/entries/${encodeURIComponent(entryId)}/vote`);
  }
  async reportEntry(entryId: string): Promise<void> {
    this.forget('contest');
    await this.req<null>('POST', `/api/contest/entries/${encodeURIComponent(entryId)}/report`);
  }
  /** Hall of Fame: every round's winner. Own winners are marked when signed in. */
  hall(): Promise<ContestEntryDTO[]> {
    return this.memo('hall', 300_000, async () => (await this.req<ContestEntriesResponse>('GET', '/api/contest/hall')).entries);
  }

  /** Who fought my monsters and the stickers they sent. */
  fanMail(): Promise<FanMailResponse> {
    return this.memo('fanmail', 120_000, () => this.req('GET', '/api/fanmail'));
  }
  /** A battle against a shared monster or a Hall of Fame winner ended. */
  async reportFight(body: FightReportRequest): Promise<boolean> {
    this.forget('fanmail');
    return (await this.req<FightReportResponse>('POST', '/api/fights', body)).counted;
  }
  async react(body: FightReactionRequest): Promise<void> {
    this.forget('fanmail');
    await this.req<null>('POST', '/api/fights/reaction', body);
  }
  dares(): Promise<DaresResponse> {
    return this.memo('dares', 30_000, () => this.req('GET', '/api/dares'));
  }
  async sendDare(body: DareCreateRequest): Promise<DareDTO> {
    this.forget('dares');
    return (await this.req<DareResponse>('POST', '/api/dares', body)).dare;
  }
  async answerDare(dareId: string, result: FightResult): Promise<DareDTO> {
    this.forget('dares');
    return (await this.req<DareResponse>('POST', `/api/dares/${encodeURIComponent(dareId)}/result`, { result })).dare;
  }
  /** Take a dare back (sender) or put it away (friend). */
  async removeDare(dareId: string): Promise<void> {
    this.forget('dares');
    await this.req<null>('DELETE', `/api/dares/${encodeURIComponent(dareId)}`);
  }
  boss(): Promise<BossResponse> {
    return this.memo('boss', 60_000, () => this.req('GET', '/api/boss'));
  }
  hitBoss(body: BossHitRequest): Promise<BossResponse> {
    this.forget('boss');
    return this.req('POST', '/api/boss/hits', body);
  }

  /** My class board (null when I am not in a class). */
  myClass(): Promise<ClassDTO | null> {
    return this.memo('class', 60_000, async () => (await this.req<ClassResponse>('GET', '/api/class')).class);
  }
  /** Start a class; the server picks the code and a safe name in lang. */
  async createClass(lang: string): Promise<ClassDTO> {
    this.forget('class');
    return (await this.req<ClassResponse>('POST', '/api/class', { lang })).class!;
  }
  async joinClass(code: string): Promise<ClassDTO> {
    this.forget('class');
    return (await this.req<ClassResponse>('POST', '/api/class/join', { code })).class!;
  }
  async leaveClass(): Promise<void> {
    this.forget('class');
    await this.req<null>('POST', '/api/class/leave');
  }
  /** Owner only: pick a new generated name. */
  async renameClass(lang: string): Promise<ClassDTO> {
    this.forget('class');
    return (await this.req<ClassResponse>('POST', '/api/class/name', { lang })).class!;
  }
  /** Owner only: take a member out (they cannot join again). */
  async kickClassMember(playerId: string): Promise<void> {
    this.forget('class');
    await this.req<null>('DELETE', `/api/class/members/${encodeURIComponent(playerId)}`);
  }
  /** A battle ended this week (counts towards the class goal). */
  classBattle(round: string): Promise<ClassBattleResponse> {
    this.forget('class');
    return this.req('POST', '/api/class/battles', { round });
  }
  async putNotebookCard(card: NotebookCardDTO): Promise<void> {
    await this.req<null>('POST', '/api/notebook/card', card);
  }
  /** A friend's notebook page, read-only (friends only). */
  friendNotebook(playerId: string): Promise<FriendNotebookResponse> {
    return this.req('GET', `/api/friends/${encodeURIComponent(playerId)}/notebook`);
  }
}
